import { z } from 'zod';
import type { ValidPageRoutes } from '../routeTypes';
import type { ComponentType, BaseSyntheticEvent } from 'react';
import type { ItemSelectionData } from '../components/tasks/ItemSelectionTask/ItemSelectionTaskSchema';
import type { ReasonData } from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import type { ResolutionData } from '../components/tasks/ResolutionTask/ResolutionTaskSchema';
import type { MethodData } from '../components/tasks/MethodTask/MethodTaskSchema';
import type { RefundData } from '../components/tasks/RefundTask/RefundTaskSchema';
import type { OrderlessCustomerData } from '../components/tasks/OrderlessTask/OrderlessTaskSchema';
import type { SignatureData } from '../components/tasks/SignatureTask/SignatureTaskSchema';
import type { ReturnPolicyData } from '../components/tasks/ReturnPolicyTask/ReturnPolicyTaskSchema';
import type { OrderlessDisclaimerData } from '../components/tasks/OrderlessDisclaimerTask/OrderlessDisclaimerTaskSchema';
import type { GiftEmailData } from '../components/tasks/GiftEmailTask/GiftEmailTaskSchema';
import type { TaskConditions } from './types';

export const authStatusSchema = z.enum(['authenticated', 'unauthenticated']);
export type AuthStatus = z.infer<typeof authStatusSchema>;

export const storedWorkflowDataSchema = z.record(z.string(), z.unknown());

export type Steps =
  | 'itemSelection'
  | 'reasons'
  | 'returnMethod'
  | 'review'
  | 'orderless';

// Workflow Types

export type Tasks =
  | 'itemSelection'
  | 'reason'
  | 'resolution'
  | 'returnMethod'
  | 'refund'
  | 'reviewItemSummary'
  | 'reviewRefundSummary'
  | 'returnPolicy'
  | 'signature'
  | 'orderlessCustomer'
  | 'orderlessDisclaimer'
  | 'giftEmail'
  /**
   * A task the server named and this build has no schema or component for.
   *
   * Client-only: the served `Tasks` enum never carries it — `getConfig` substitutes it for
   * an id it does not recognise, so an unknown task reaches the renderer as *something*
   * instead of being deleted. A member rather than widening this union to `string`, which
   * would collapse the `Extract<Tasks, 'reason'>` idioms in `queries.ts`,
   * `ReasonsTaskSchema` and `ResolutionTaskSchema` to `never`.
   */
  | 'unsupported';

export type TaskScope = 'cart' | 'item';

/**
 * Whether a task takes part in a step, and whether the shopper sees it. Whether it is
 * *validated* is decided by its `schema`, not by this — an enabled task with no schema
 * renders and validates nothing.
 * - 'enabled': visible, and takes part
 * - 'disabled': absent; neither rendered nor validated
 * - 'implicit': takes part without ever being visible
 */
export type TaskMode = 'enabled' | 'disabled' | 'implicit';

export type TaskBaseConfig = {
  id: Tasks;
  guards?: Tasks[] | null;
  mode: TaskMode;
  scope: TaskScope;
  conditions?: TaskConditions | null;
};

export type TaskSchemaConfig<T = unknown> = {
  id: Tasks;
  /**
   * `null` for a task that renders but collects nothing — a content block. Every
   * consumer treats that as "no rule can fail", so it never contributes an error,
   * never blocks a step, and never enters the form schema.
   *
   * A permissive schema does not do the same job: it still makes the task a field, so
   * it enters the form shape, and for an item-scoped task it must also be present in
   * the data to read as complete. `null` is the only form of "no rules" the compiler
   * can check. `z.object({}).optional()` was how these tasks were expressed before,
   * and it throws on the `null` fed to a cart task whose data is absent, which left
   * any step holding one permanently invalid.
   *
   * Required-and-nullable rather than optional, so a forgotten schema stays a
   * compile error instead of silently becoming a task that cannot fail.
   */
  schema: z.ZodType<T> | null;
};

export type TaskConfig = TaskBaseConfig & TaskSchemaConfig;

export type StepBaseConfig = {
  id: Steps;
  route: ValidPageRoutes;
};

export type StepConfig = StepBaseConfig & {
  tasks: TaskConfig[];
};

export type Workflow = {
  steps: StepConfig[];
};

export type TaskComponentType = ComponentType<{
  handleNext: (e?: BaseSyntheticEvent) => Promise<void>;
  itemId?: string;
  isLoading?: boolean;
}>;

export type TaskComponentMap = {
  [K in Tasks]: TaskComponentType;
};

// Workflow Data Types

type AllWorkflowDataTypes = ItemSelectionData &
  ReasonData &
  ResolutionData &
  MethodData &
  RefundData &
  OrderlessCustomerData &
  OrderlessDisclaimerData &
  SignatureData &
  ReturnPolicyData &
  GiftEmailData;

export type ItemId = ItemSelectionData['itemSelection']['id'];

export type WorkflowItemData = Partial<AllWorkflowDataTypes>;

export type WorkflowData = {
  items?: Record<string, Partial<AllWorkflowDataTypes>>;
} & Partial<Omit<AllWorkflowDataTypes, 'itemSelection'>>;

export const hasItemsSelected = (data: WorkflowData | null): boolean => {
  return Boolean(data?.items && Object.keys(data.items).length > 0);
};
